<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.update")) + " " + $tc("pbx_master_account.title", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide"/>
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            name="tenant"
            rules="required"
          >
            <b-form-group
              label="Tenant"
              label-for="id_tenant"
              :state="getValidationState(validationContext)"
            >
              <v-select
                data-cy="tenant-sidenav"
                v-model="itemData.id_tenant"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="tenantOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="id_tenant"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider #default="validationContext" name="name" rules="required">
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- domain -->
          <validation-provider
            #default="validationContext"
            name="domain"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.domain')"
              label-for="domain"
            >
              <b-form-input
                id="domain"
                v-model="itemData.domain"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- external_sip_ip -->
          <validation-provider
            #default="validationContext"
            name="external_sip_ip"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.external_sip_ip')"
              label-for="external_sip_ip"
            >
              <b-form-input
                id="external_sip_ip"
                v-model="itemData.external_sip_ip"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- external_rtp_ip -->
          <validation-provider
            #default="validationContext"
            name="external_rtp_ip"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.external_rtp_ip')"
              label-for="external_rtp_ip"
            >
              <b-form-input
                id="external_rtp_ip"
                v-model="itemData.external_rtp_ip"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- sip_port -->
          <validation-provider
            #default="validationContext"
            name="sip_port"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.sip_port')"
              label-for="sip_port"
            >
              <b-form-input
                id="sip_port"
                v-model="itemData.sip_port"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- tls_sip_port -->
          <validation-provider
            #default="validationContext"
            name="tls_sip_port"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.tls_sip_port')"
              label-for="tls_sip_port"
            >
              <b-form-input
                id="tls_sip_port"
                v-model="itemData.tls_sip_port"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- ws_url -->
          <validation-provider
            #default="validationContext"
            name="ws_url"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.ws_url')"
              label-for="ws_url"
            >
              <b-form-input
                id="ws_url"
                v-model="itemData.ws_url"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- ws_binding_port -->
          <validation-provider
            #default="validationContext"
            name="ws_binding_port"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.ws_binding_port')"
              label-for="ws_binding_port"
            >
              <b-form-input
                id="ws_binding_port"
                v-model="itemData.ws_binding_port"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- wss_binding_port -->
          <validation-provider
            #default="validationContext"
            name="wss_binding_port"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.wss_binding_port')"
              label-for="wss_binding_port"
            >
              <b-form-input
                id="wss_binding_port"
                v-model="itemData.wss_binding_port"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- api_url -->
          <validation-provider
            #default="validationContext"
            name="api_url"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pbx_master_account.api_url')"
              label-for="api_url"
            >
              <b-form-input
                id="api_url"
                v-model="itemData.api_url"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- call_recording -->
          <validation-provider
            name="call_recording"
            rules="required"
            vid="call_recording"
          >
            <b-form-checkbox
              id="call_recording"
              v-model="itemData.call_recording"
              name="call_recording"
              value="true"
              class="my-1 text-capitalize"
              unchecked-value="false"
              switch
            >
              {{ $t("pbx_master_account.call_recording") }}
            </b-form-checkbox>
          </validation-provider>

          <!-- queue timeout -->
          <validation-provider
            #default="validationContext"
            name="queue_timeout"
            vid="queue_timeout"
          >
            <b-form-group
              class="text-capitalize"
              :label="`${$t('pbx_master_account.queue_timeout')} (${$t('pbx_master_account.seconds')})`"
              label-for="queue_timeout"
            >
              <b-form-input
                id="api_url"
                v-model="itemData.queue_timeout"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>

          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              data-cy="primary-button"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import {ValidationProvider, ValidationObserver} from "vee-validate";
import vSelect from "vue-select";
import {ref} from "@vue/composition-api";
import {required, alphaNum, email, positive, required_if} from "@validations";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, {emit}) {
    const blankData = {
      name: "",
      domain: "",
      ws_url: "",
      sip_port: 5060,
      tls_sip_port: 5061,
      ws_binding_port: 5066,
      wss_binding_port: 7443,
      external_sip_ip: "",
      external_rtp_ip: "",
      call_recording: false,
      queue_timeout: null,
      api_url: "",
      id_tenant: null,
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        dispatch = store.dispatch("pbx_master_account/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: itemData.value,
        };

        dispatch = store.dispatch("pbx_master_account/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.status) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const {refFormObserver, getValidationState, resetForm} = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
      }
    };

    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>
